"use client";

import { useEffect, useId, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import styles from "./walkthrough-spotlight.module.css";

type Box = { x: number; y: number; width: number; height: number };
type Spotlight = { targets: Box[]; context: Box[]; label: string; width: number; height: number };

function visible(element: HTMLElement) {
  return element.getClientRects().length > 0 && getComputedStyle(element).visibility !== "hidden";
}

function box(element: HTMLElement, padding = 7): Box {
  const rect = element.getBoundingClientRect();
  return { x: rect.left - padding, y: rect.top - padding, width: rect.width + padding * 2, height: rect.height + padding * 2 };
}

export function WalkthroughSpotlight({ root, target, label, enabled }: {
  root: RefObject<HTMLDivElement | null>; target: string; label: string; enabled: boolean;
}) {
  const maskId = useId();
  const [view, setView] = useState<Spotlight | null>(null);

  useEffect(() => {
    if (!enabled) { setView(null); return; }
    let frame = 0;
    let lastTarget: HTMLElement | undefined;
    let lastLabel = "";
    let observed: HTMLElement[] = [];
    const resize = new ResizeObserver(() => schedule());
    const measure = () => {
      frame = 0;
      const section = root.current;
      if (!section) return;
      const bounds = section.getBoundingClientRect();
      const dialog = Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"][data-state="open"]')).filter(visible).at(-1);
      if (!dialog && (bounds.top > window.innerHeight - 160 || bounds.bottom < 160)) { setView(null); return; }
      const scope = dialog ?? section;
      const override = scope.querySelector<HTMLElement>("[data-spotlight-target]");
      const targets = override ? [override] : dialog ? [] : Array.from(section.querySelectorAll<HTMLElement>(`[data-practice-target="${target}"]`)).filter(visible);
      if (!targets.length && !dialog) {
        const restore = section.querySelector<HTMLElement>('[data-practice-target="restore"]');
        if (restore) targets.push(restore);
      }
      if (!targets.length) { setView(null); return; }
      const nextLabel = override?.dataset.spotlightTarget ?? label;
      const contexts = Array.from(scope.querySelectorAll<HTMLElement>("[data-spotlight-context], [role=status]")).filter(visible);
      const primary = targets[0];
      const guide = contexts.find((element) => element.dataset.spotlightContext === "guide");
      const guideBottom = guide ? guide.getBoundingClientRect().height + 50 : 38;
      if (primary !== lastTarget || nextLabel !== lastLabel) {
        lastTarget = primary; lastLabel = nextLabel;
        const overview = !dialog && !override ? section.querySelector<HTMLElement>('[data-spotlight-context="overview"]') : null;
        const bounds = [...targets, ...(overview ? [overview] : [])].map((element) => element.getBoundingClientRect());
        const rect = { top: Math.min(...bounds.map((item) => item.top)), bottom: Math.max(...bounds.map((item) => item.bottom)), height: 0 };
        rect.height = rect.bottom - rect.top;
        if (dialog) primary.scrollIntoView({ block: "nearest", behavior: "instant" });
        else if (rect.top < guideBottom || rect.bottom > window.innerHeight - 40) {
          const top = Math.min(guideBottom, window.innerHeight * .45);
          const distance = rect.top < guideBottom || rect.height > window.innerHeight - top - 40 ? rect.top - top : rect.bottom - window.innerHeight + 40;
          window.scrollBy({ top: distance, behavior: "instant" });
        }
      }
      const all = [...targets, ...contexts, section];
      if (all.length !== observed.length || all.some((element, index) => element !== observed[index])) {
        resize.disconnect(); all.forEach((element) => resize.observe(element)); observed = all;
      }
      setView({ targets: targets.map((element) => box(element)), context: contexts.map((element) => box(element, 3)), label: nextLabel, width: window.innerWidth, height: window.innerHeight });
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
    const mutations = new MutationObserver((records) => {
      if (records.some((record) => !(record.target instanceof Element && record.target.closest("[data-walkthrough-spotlight]")))) schedule();
    });
    mutations.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ["data-state", "data-spotlight-target", "data-practice-target", "class", "style"] });
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    document.addEventListener("animationend", schedule, true);
    schedule();
    return () => {
      cancelAnimationFrame(frame); resize.disconnect(); mutations.disconnect();
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      document.removeEventListener("animationend", schedule, true);
    };
  }, [root, target, label, enabled]);

  if (!enabled || !view) return null;
  const anchor = view.targets.find((rect) => rect.y >= 32 && rect.y < view.height - 20);
  const hasContext = anchor && view.context.some((rect) => rect.x <= anchor.x && rect.y < anchor.y && rect.x + rect.width >= anchor.x + anchor.width && rect.y + rect.height >= anchor.y + anchor.height);
  return createPortal(<div className={styles.layer} data-walkthrough-spotlight aria-hidden="true">
    <svg width={view.width} height={view.height} className={styles.overlay}>
      <defs><mask id={maskId}><rect width="100%" height="100%" fill="white" />{[...view.targets, ...view.context].map((rect, index) => <rect key={index} {...rect} rx="9" fill="black" />)}</mask></defs>
      <rect width="100%" height="100%" fill="#020907" fillOpacity=".76" mask={`url(#${maskId})`} />
      {view.targets.map((rect, index) => <rect key={index} {...rect} rx="9" className={styles.ring} />)}
    </svg>
    {anchor && !hasContext && <div className={styles.label} style={{ left: Math.max(10, Math.min(anchor.x, view.width - 190)), top: anchor.y - 28 }}>{view.label} <span>↓</span></div>}
  </div>, document.body);
}
